import { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import { ICONS_WEB, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { getRootUrl } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * Image component to display an image with optional label and styles.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.image - The document object of the image.
 * @param {Object} props.image.referenceId - The source URL of the image.
 * @param {function} [props.onClick] - The image click handler function.
 * @param {Object} [props.style] - The optional style object for the <img>.
 * @param {string} [props.containerClass] - The optional className string for the container.
 * @param {string} [props.altText] - The alt text for the image.
 * @param {Function} [props.onEdit] - The function to call when the edit button is clicked.
 * @param {Function} [props.onDelete] - The function to call when the delete button is clicked.
 * @param {boolean} [props.disabled] - Whether the edit and delete buttons are disabled.
 * @param {boolean} [props.isEditing] - Whether the component is in editing mode.
 * @param {string} [props.fileName] - The optional file name to display below the image.
 * @returns {JSX.Element} The rendered Image component.
 */
export const Image = ({
  image = {},
  onClick,
  style = {},
  containerClass = "",
  altText = "",
  onEdit = () => null,
  onDelete = () => null,
  disabled = false,
  isEditing = false,
  fileName = "",
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const API_URL = getRootUrl();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <Box
        className={`${classes.imgContainer} ${containerClass}`}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {image?.referenceId || typeof image === "string" ? (
          <img
            src={
              typeof image === "string"
                ? image
                : `${API_URL}/${image.referenceId}`
            }
            width="fit-content"
            height="100%"
            className={classes.imgStyle}
            style={{
              ...style,
              cursor: onClick ? "pointer" : "default",
            }}
            alt={altText}
          />
        ) : (
          <MiIcon
            path={mdi[ICONS_WEB.IMAGE_OFF]}
            size={1.5}
            color={theme.palette.grey.main}
          />
        )}
        {isEditing && isHovered ? (
          <>
            {onEdit && (
              <IconButton
                size="small"
                disabled={disabled}
                className={classes.editButton}
                onClick={onEdit}
              >
                <MiIcon path={mdi[ICONS_WEB.PENCIL]} size={0.75} />
              </IconButton>
            )}
            {onDelete && image?.referenceId ? (
              <IconButton
                size="small"
                disabled={disabled}
                className={classes.deleteButton}
                onClick={onDelete}
              >
                <MiIcon
                  path={mdi[ICONS_WEB.TRASH_CAN]}
                  size={0.75}
                  color={theme.palette.red.dark}
                />
              </IconButton>
            ) : null}
          </>
        ) : null}
      </Box>
      {fileName && (
        <Box className={classes.fileNameContainer}>
          <MiIcon
            path={mdi.mdiImageOutline}
            color={theme.palette.green.main}
            size={0.8}
          />
          <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
            {fileName}
          </Typography>
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  imgContainer: {
    width: "100%",
    height: 200,
    position: "relative",
    display: "grid",
    placeContent: "center",
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 6,
    overflow: "hidden",
  },
  imgStyle: {
    objectFit: "contain",
    width: "100%",
    aspectRatio: 1,
  },
  editButton: {
    position: "absolute",
    bottom: 2,
    right: 2,
    zIndex: 2,
    backgroundColor: "rgba(0,0,0,0.5)",
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.35)",
    },
    "&:disabled": {
      backgroundColor: "rgba(0,0,0,0.15)",
      color: theme.palette.common.white,
    },
  },
  deleteButton: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 2,
    "&:disabled": {
      color: theme.palette.red.accent,
    },
  },
  fileNameContainer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
  },
}));

Image.propTypes = {
  image: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  style: PropTypes.object,
  containerClass: PropTypes.string,
  altText: PropTypes.string,
};
